// ui/pause.ts — the pause menu (DESIGN §2: "Esc pause").
//
// DOM over the frozen canvas, like the altar overlay: game.ts stops stepping the
// sim while this is up. It is also the in-run reference the game otherwise
// lacks — the full control list, what the potion does, and every relic the
// Warden is carrying with its actual effect. Abandoning from here ends the run
// through the normal endRun path, so embers bank and the summary shows.
//
// Esc toggling lives in game.ts (metaKeyPressed); this module only renders and
// owns its buttons/sliders.

import { meta } from '../game/meta'
import { heldRelics } from '../game/relics'
import { RELIC_RARITY_COLOR } from '../data/relics'
import { getWeapon } from '../game/weapons'
import { TUNING } from '../data/tuning'
import { audio } from '../engine/audio'
import { fx } from '../engine/fx'
import type { Run } from '../game/game'

let root: HTMLElement | null = null
let panel: HTMLElement | null = null
let armedAbandon = false

function ui(): HTMLElement {
  if (!root) root = document.getElementById('ui') as HTMLElement
  return root
}

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

const CONTROLS: [string, string][] = [
  ['WASD / ARROWS', 'move'],
  ['MOUSE', 'aim'],
  ['LMB', 'attack — hold to keep swinging'],
  ['RMB', 'special attack (cooldown)'],
  ['SPACE', 'dash — brief invulnerability'],
  ['Q', `quaff potion — Emberburst: ${TUNING.player.potionDamage} damage around you, destroys enemy shots, staggers`],
  ['E', 'interact — levers, altars, fountains, husks, vault doors'],
  ['ESC', 'this menu'],
]

function controlRows(run: Run): string {
  return CONTROLS.map(([key, what]) => {
    // RMB names the equipped form's actual special, so the row teaches the
    // weapon in hand rather than a generic verb.
    if (key === 'RMB') {
      const w = getWeapon(run.player.weaponId)
      what = `special — ${w.name}: ${w.specialName} (${w.specialCooldown}s cooldown)`
    }
    return `<div class="pz-key">${esc(key)}</div><div class="pz-what">${esc(what)}</div>`
  }).join('')
}

function relicRows(): string {
  const held = heldRelics()
  if (held.length === 0) {
    return '<div class="pz-empty">None yet. Bosses offer a free pick; respite altars sell them for gold.</div>'
  }
  return held
    .map(
      (r) => `<div class="pz-relic">
        <span class="pz-relic-name" style="color:${RELIC_RARITY_COLOR[r.rarity]}">${esc(r.name)}</span>
        <span class="pz-relic-desc">${esc(r.desc)}</span>
      </div>`,
    )
    .join('')
}

function carriedRows(run: Run): string {
  const p = run.player
  const rows = [
    `<div class="pz-key">EMBERS</div><div class="pz-what"><b class="ember">${run.embers}</b> this run — banked even if you fall</div>`,
    `<div class="pz-key">GOLD</div><div class="pz-what"><b>${p.gold}</b> — in-run only, lost on death; spend it at respite altars</div>`,
    `<div class="pz-key">POTIONS</div><div class="pz-what"><b>${p.potions}/${p.potionCap}</b> carried</div>`,
  ]
  if (p.keys > 0) {
    rows.push(`<div class="pz-key">KEYS</div><div class="pz-what"><b>${p.keys}</b> — something in this zone is locked</div>`)
  }
  return rows.join('\n')
}

export function showPause(run: Run, onResume: () => void, onAbandon: () => void): void {
  hidePause()
  armedAbandon = false
  const el = document.createElement('div')
  el.className = 'screen overlay pause'
  el.id = 'pause'
  el.innerHTML = `
    <h2 class="ov-title">PAUSED</h2>
    <p class="ov-sub">The deep holds its breath. Nothing moves until you do.</p>

    <div class="pz-cols">
      <section>
        <h3 class="pz-h">CONTROLS</h3>
        <div class="pz-grid">${controlRows(run)}</div>
        <h3 class="pz-h">CARRIED</h3>
        <div class="pz-grid">${carriedRows(run)}</div>
      </section>
      <section>
        <h3 class="pz-h">RELICS HELD <span class="pz-dim">— ${heldRelics().length}</span></h3>
        <div class="pz-relics">${relicRows()}</div>
        <h3 class="pz-h">OPTIONS</h3>
        <div class="pz-opts">
          <label>VOLUME <input type="range" id="pz-volume" min="0" max="1" step="0.05" value="${meta.data.settings.volume}" /></label>
          <label>SCREEN SHAKE <input type="range" id="pz-shake" min="0" max="1" step="0.05" value="${meta.data.settings.shake}" /></label>
        </div>
      </section>
    </div>

    <div class="pz-actions">
      <button class="btn" id="pz-resume">RESUME&nbsp;&nbsp;(ESC)</button>
      <button class="btn pz-abandon" id="pz-abandon">ABANDON RUN — BANK ${run.embers} EMBERS</button>
    </div>
  `
  ui().appendChild(el)
  panel = el

  el.querySelector('#pz-resume')?.addEventListener('click', () => onResume())
  const abandonBtn = el.querySelector('#pz-abandon') as HTMLButtonElement | null
  abandonBtn?.addEventListener('click', () => {
    // Two clicks: a 30-minute run must not die to a stray click.
    if (!armedAbandon) {
      armedAbandon = true
      abandonBtn.textContent = 'YOU ARE SURE? — CLICK AGAIN TO END THE RUN'
      abandonBtn.classList.add('is-armed')
      audio.sfx('ui')
      return
    }
    onAbandon()
  })

  const vol = el.querySelector('#pz-volume') as HTMLInputElement | null
  vol?.addEventListener('input', () => {
    const v = Number(vol.value)
    audio.setVolume(v)
    meta.setSettings(v, meta.data.settings.shake)
    audio.sfx('ui')
  })
  const shake = el.querySelector('#pz-shake') as HTMLInputElement | null
  shake?.addEventListener('input', () => {
    const v = Number(shake.value)
    fx.shakeScale = v
    meta.setSettings(meta.data.settings.volume, v)
  })
}

export function hidePause(): void {
  if (panel && panel.parentElement) panel.parentElement.removeChild(panel)
  panel = null
  armedAbandon = false
}

export function pauseVisible(): boolean {
  return panel != null
}
